import { useState, useCallback, useEffect, useMemo } from 'react';
import { useAuth } from './useAuth';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { updatePostCommentCount } from '../store/slices/feedSlice';
import { updatePublicPostCommentCount } from '../store/slices/publicFeedSlice';
import { selectShadowbannedUids } from '../store/slices/shadowbanSlice';
import logger from '../utils/logger';
import {
  Comment,
  CommentQueryOptions,
  addComment,
  getComments,
  getReplies,
  updateComment,
  deleteComment
  // Comment likes removed - not in original app
  // toggleLikeComment
} from '../services/firebase/comments.service';

// Module-level cache to prevent multiple fetches for the same content
// across component remounts and different instances.
// The cache must hold the DATA, not just a "fetched" flag: comment state is
// per hook instance, so a flag-only cache made remounted instances skip the
// fetch and render zero comments.
const fetchedCommentsCache = new Map<string, any[]>();
// In-flight fetches keyed by contentId. Stores the pending PROMISE (not a
// boolean) so an instance mounting while another instance's fetch is in
// flight can await the same request and hydrate its own state - a flag-only
// cache made late arrivals skip the fetch and render empty comments forever.
const fetchInProgressCache = new Map<string, Promise<Comment[]>>();

// Clear cache for a specific contentId (e.g., when user posts a comment and wants to refetch)
export const clearCommentsCache = (contentId: string) => {
  fetchedCommentsCache.delete(contentId);
  fetchInProgressCache.delete(contentId);
};

// Clear all comment caches (e.g., on logout to prevent memory leaks)
export const clearAllCommentsCaches = () => {
  fetchedCommentsCache.clear();
  fetchInProgressCache.clear();
};

export interface PostDetails {
  owner?: string;
  title?: string;
}

export const useComments = (contentType: string, contentId: string, postDetails?: PostDetails) => {
  const { user } = useAuth();
  const dispatch = useAppDispatch();
  const [rawComments, setRawComments] = useState<Comment[]>([]);
  const [rawReplies, setRawReplies] = useState<Record<string, Comment[]>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get shadowban data for filtering
  const shadowbannedUids = useAppSelector(selectShadowbannedUids);
  const currentProfile = useAppSelector((state) => state.user?.currentProfile);
  const isCurrentUserAdmin = currentProfile?.isAdmin || false;

  // Convert shadowban array to Set once for O(1) lookups instead of O(n)
  const shadowbannedSet = useMemo(
    () => new Set(shadowbannedUids),
    [shadowbannedUids]
  );

  // Filter comments to hide shadowbanned users' comments
  // SHADOWBAN FILTERING:
  // - Non-admin users: Hide comments from shadowbanned users (unless it's their own comment)
  // - Admin users: See all comments (with indicator added later)
  // - Users always see their own comments regardless of shadowban status
  const comments = useMemo(() => {
    return rawComments.filter(comment => {
      const authorUid = comment.author?.uid;
      if (!authorUid) return true; // Keep comments without author info

      const isShadowbanned = shadowbannedSet.has(authorUid);
      const isOwnComment = authorUid === user?.uid;

      // Admins see all, users see non-shadowbanned + their own
      if (isShadowbanned && !isCurrentUserAdmin && !isOwnComment) {
        return false;
      }
      return true;
    }).map(comment => ({
      ...comment,
      // Add shadowban indicator for admin UI
      isShadowbanned: shadowbannedSet.has(comment.author?.uid || '')
    }));
  }, [rawComments, shadowbannedSet, isCurrentUserAdmin, user?.uid]);

  // Filter replies the same way
  const replies = useMemo(() => {
    const filtered: Record<string, Comment[]> = {};
    Object.entries(rawReplies).forEach(([parentId, parentReplies]) => {
      filtered[parentId] = parentReplies.filter(reply => {
        const authorUid = reply.author?.uid;
        if (!authorUid) return true;

        const isShadowbanned = shadowbannedSet.has(authorUid);
        const isOwnReply = authorUid === user?.uid;

        if (isShadowbanned && !isCurrentUserAdmin && !isOwnReply) {
          return false;
        }
        return true;
      }).map(reply => ({
        ...reply,
        isShadowbanned: shadowbannedSet.has(reply.author?.uid || '')
      }));
    });
    return filtered;
  }, [rawReplies, shadowbannedSet, isCurrentUserAdmin, user?.uid]);

  // Fetch all comments (can be called manually to refresh)
  const fetchComments = useCallback(async (options?: CommentQueryOptions) => {
    if (!contentId) return;

    setIsLoading(true);
    setError(null);

    try {
      // For comments, contentId is the postKey
      const data = await getComments(contentId, options?.limit);
      setRawComments(data);
      fetchedCommentsCache.set(contentId, data);
    } catch (err) {
      logger.error('Error fetching comments:', err);
      setError('Failed to load comments');
    } finally {
      setIsLoading(false);
    }
  }, [contentId]);

  // Fetch comments on mount - only once per contentId across ALL instances
  useEffect(() => {
    // Skip if no contentId
    if (!contentId) return;

    // Cache hit: hydrate this instance's state from the cached data
    const cached = fetchedCommentsCache.get(contentId);
    if (cached) {
      setRawComments(cached);
      return;
    }

    // Reuse an in-flight fetch if one exists; otherwise start one. Either way
    // THIS instance awaits the shared promise and sets its own state from the
    // result, so concurrent mounts for the same contentId all get data.
    let fetchPromise = fetchInProgressCache.get(contentId);
    if (!fetchPromise) {
      fetchPromise = getComments(contentId)
        .then(data => {
          // Cache the data for other/future instances
          fetchedCommentsCache.set(contentId, data);
          return data;
        })
        .finally(() => {
          // Remove from in-progress
          fetchInProgressCache.delete(contentId);
        });
      fetchInProgressCache.set(contentId, fetchPromise);
    }

    setIsLoading(true);
    fetchPromise
      .then(data => {
        setRawComments(data);
      })
      .catch(err => {
        logger.error('Error fetching comments:', err);
        setError('Failed to load comments');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [contentId]);
  
  // Fetch replies for a comment
  const fetchReplies = useCallback(async (commentId: string, options?: CommentQueryOptions) => {
    setIsLoading(true);
    
    try {
      // For replies, we need both postKey (contentId) and the parent comment ID
      const data = await getReplies(contentId, commentId);
      
      setRawReplies(prevReplies => ({
        ...prevReplies,
        [commentId]: data
      }));
      
      // Temporarily disable real-time subscription to prevent console errors
      // Set up subscription for real-time updates to this reply thread would go here
      
      return data;
    } catch (err) {
      logger.error(`Error fetching replies for comment ${commentId}:`, err);
      setError('Failed to load replies');
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [contentId]);
  
  // Add a new comment
  const postComment = useCallback(async (content: string, parentId?: string) => {
    if (!user || !contentId) {
      setError('You must be logged in to comment');
      return null;
    }

    try {
      // Add comment - pass postDetails for notifications
      const commentId = await addComment(contentId, content, {
        owner: postDetails?.owner,
        title: postDetails?.title,
        type: contentType
      });

      // Clear cache so next mount will refetch fresh data
      clearCommentsCache(contentId);
      
      // Create a temporary comment object for immediate UI update
      const newComment: Comment = {
        id: commentId,
        timestamp: Date.now(),
        author: {
          uid: user.uid,
          username: user.displayName || 'User'
        },
        content: content,
        post: {
          title: postDetails?.title || '',
          type: contentType,
          owner: postDetails?.owner || ''
        },
        isReported: false
        // Comment likes removed - not in original app
        // likeCount: 0
      };
      
      // If this is a reply, update the replies state
      if (parentId) {
        setRawReplies(prevReplies => {
          const existingReplies = prevReplies[parentId] || [];
          return {
            ...prevReplies,
            [parentId]: [...existingReplies, newComment]
          };
        });
      } else {
        // Update comments state with the new comment
        setRawComments(prevComments => [...prevComments, newComment]);
      }

      // Update feed slices so comment count reflects immediately when navigating back
      dispatch(updatePostCommentCount({ postId: contentId, delta: 1 }));
      dispatch(updatePublicPostCommentCount({ postId: contentId, delta: 1 }));

      return newComment;
    } catch (err) {
      logger.error('Error posting comment:', err);
      setError('Failed to post comment');
      return null;
    }
  }, [user, contentId, contentType, postDetails, dispatch]);
  
  // Update an existing comment
  const editComment = useCallback(async (commentId: string, content: string) => {
    if (!user) {
      setError('You must be logged in to edit a comment');
      return null;
    }
    
    try {
      // updateComment returns void, so we need to manually update state
      await updateComment(contentId, commentId, content);

      // Check if this is a top-level comment or a reply
      const commentIndex = rawComments.findIndex(c => c.id === commentId);

      if (commentIndex !== -1) {
        // It's a top-level comment
        setRawComments(prevComments => {
          const newComments = [...prevComments];
          newComments[commentIndex] = {
            ...newComments[commentIndex],
            content: content,
            isEdited: true,
            editedAt: Date.now()
          };
          return newComments;
        });
        return rawComments[commentIndex];
      } else {
        // It's a reply, find which parent it belongs to
        let updatedComment: Comment | null = null;
        Object.keys(rawReplies).forEach(parentId => {
          const replyIndex = rawReplies[parentId].findIndex(r => r.id === commentId);

          if (replyIndex !== -1) {
            setRawReplies(prevReplies => {
              const newReplies = { ...prevReplies };
              const parentReplies = [...newReplies[parentId]];
              parentReplies[replyIndex] = {
                ...parentReplies[replyIndex],
                content: content,
                isEdited: true,
                editedAt: Date.now()
              };
              updatedComment = parentReplies[replyIndex];
              newReplies[parentId] = parentReplies;
              return newReplies;
            });
          }
        });
        return updatedComment;
      }
    } catch (err) {
      logger.error(`Error updating comment ${commentId}:`, err);
      setError('Failed to update comment');
      return null;
    }
  }, [user, contentId, rawComments, rawReplies]);
  
  // Delete a comment
  const removeComment = useCallback(async (commentId: string) => {
    if (!user) {
      setError('You must be logged in to delete a comment');
      return false;
    }
    
    try {
      await deleteComment(contentId, commentId);
      
      // Remove from state
      // For top-level comments
      setRawComments(prevComments => prevComments.filter(c => c.id !== commentId));
      
      // For replies
      setRawReplies(prevReplies => {
        const newReplies = { ...prevReplies };

        // Check each parent's replies
        Object.keys(newReplies).forEach(parentId => {
          // If this is the parent that was deleted, remove all its replies
          if (parentId === commentId) {
            delete newReplies[parentId];
          } else {
            // Filter out the reply if it was in this parent
            newReplies[parentId] = newReplies[parentId].filter(r => r.id !== commentId);
          }
        });

        return newReplies;
      });

      // Update feed slices so comment count reflects immediately when navigating back
      dispatch(updatePostCommentCount({ postId: contentId, delta: -1 }));
      dispatch(updatePublicPostCommentCount({ postId: contentId, delta: -1 }));

      return true;
    } catch (err) {
      logger.error(`Error deleting comment ${commentId}:`, err);
      setError('Failed to delete comment');
      return false;
    }
  }, [user, contentId, dispatch]);
  
  // Comment likes removed - not in original app
  // const likeComment = useCallback(async (commentId: string, like: boolean) => {
  //   if (!user) {
  //     setError('You must be logged in to like a comment');
  //     return false;
  //   }
  //   
  //   try {
  //     // toggleLikeComment only takes postKey and commentId, it determines like/unlike based on current state
  //     await toggleLikeComment(contentId, commentId);
  //     
  //     // Update comment in state
  //     const updateCommentState = (comment: Comment) => {
  //       const currentLikes = comment.likes || {};
  //       const currentLikeCount = comment.likeCount || 0;
  //       
  //       const newLikes = { ...currentLikes };
  //       
  //       if (like) {
  //         newLikes[user.uid] = true;
  //       } else {
  //         delete newLikes[user.uid];
  //       }
  //       
  //       return {
  //         ...comment,
  //         likes: newLikes,
  //         likeCount: like ? currentLikeCount + 1 : Math.max(0, currentLikeCount - 1)
  //       };
  //     };
  //     
  //     // Check top-level comments
  //     const commentIndex = comments.findIndex(c => c.id === commentId);
  //     if (commentIndex !== -1) {
  //       setRawComments(prevComments => {
  //         const newComments = [...prevComments];
  //         newComments[commentIndex] = updateCommentState(newComments[commentIndex]);
  //         return newComments;
  //       });
  //     } else {
  //       // Check replies
  //       Object.keys(replies).forEach(parentId => {
  //         const replyIndex = replies[parentId].findIndex(r => r.id === commentId);
  //         
  //         if (replyIndex !== -1) {
  //           setRawReplies(prevReplies => {
  //             const newReplies = { ...prevReplies };
  //             const parentReplies = [...newReplies[parentId]];
  //             parentReplies[replyIndex] = updateCommentState(parentReplies[replyIndex]);
  //             newReplies[parentId] = parentReplies;
  //             return newReplies;
  //           });
  //         }
  //       });
  //     }
  //   
  //   return true;
  // } catch (err) {
  //   logger.error(`Error liking/unliking comment ${commentId}:`, err);
  //   setError('Failed to update like');
  //   return false;
  // }
  // }, [user, comments, replies]);
  
  // Comment likes removed - not in original app
  // const hasUserLiked = useCallback((commentId: string): boolean => {
  //   if (!user) return false;
  //   
  //   // Check top-level comments
  //   const comment = comments.find(c => c.id === commentId);
  //   if (comment) {
  //     return comment.likes ? !!comment.likes[user.uid] : false;
  //   }
  //   
  //   // Check replies
  //   for (const parentId in replies) {
  //     const reply = replies[parentId].find(r => r.id === commentId);
  //     if (reply) {
  //       return reply.likes ? !!reply.likes[user.uid] : false;
  //     }
  //   }
  //   
  //   return false;
  // }, [user, comments, replies]);
  
  return {
    comments,
    replies,
    isLoading,
    error,
    fetchComments,
    fetchReplies,
    postComment,
    editComment,
    removeComment,
    // Comment likes removed - not in original app
    // likeComment,
    // hasUserLiked
  };
};